// Login.js
import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { isValidPrivateKeyFormat } from './utils/secureStorage';

const Login = () => {
  const [privateKeyInput, setPrivateKeyInput] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, createAccount } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async () => {
    setError('');
    
    const trimmedKey = privateKeyInput.trim();
    
    // Basic validation before attempting login
    if (!trimmedKey) {
      setError('Please enter your private key');
      return;
    }
    
    // Validate format (64 hex characters)
    if (!isValidPrivateKeyFormat(trimmedKey)) {
      setError('Invalid private key format. Must be 64 hexadecimal characters.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (login(trimmedKey)) {
        // Clear the input immediately after successful login
        setPrivateKeyInput('');
        navigate('/');
      } else {
        setError('Invalid private key. Please check and try again.');
      }
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    setError('');
    setIsLoading(true);
    
    try {
      const result = createAccount();
      if (result && result.privateKey) {
        // Login with the new key
        if (login(result.privateKey)) {
          navigate('/profile-setup');
        } else {
          setError('Failed to initialize new account');
        }
      } else {
        setError('Failed to create account. Please try again.');
      }
    } catch {
      setError('Account creation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Clear input on unmount to prevent memory leaks of sensitive data
  React.useEffect(() => {
    return () => {
      setPrivateKeyInput('');
    };
  }, []);

  return (
    <div className="auth-container" role="main" aria-labelledby="login-heading">
      <div className="auth-card">
        <h2 id="login-heading">Welcome to Govstr</h2>
        
        <p 
          id="login-description"
          style={{ 
            fontSize: '0.875rem', 
            color: 'var(--text-secondary)', 
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}
        >
          Sign in with your Nostr private key or create a new account.
        </p>
        
        <form 
          className="auth-form" 
          onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
          aria-describedby="login-description"
        >
          <label htmlFor="private-key-input" className="visually-hidden">
            Private Key
          </label>
          <input
            id="private-key-input"
            type="password"
            placeholder="Enter your private key (nsec or hex)"
            value={privateKeyInput}
            onChange={(e) => {
              setPrivateKeyInput(e.target.value);
              if (error) setError('');
            }}
            onKeyPress={handleKeyPress}
            className="auth-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            disabled={isLoading}
            aria-describedby={error ? "login-error" : "security-note"}
            aria-invalid={error ? "true" : "false"}
          />
          
          {error && (
            <div 
              id="login-error" 
              className="auth-error" 
              role="alert" 
              aria-live="polite"
            >
              {error}
            </div>
          )}
          
          <button 
            type="submit"
            className="auth-button"
            disabled={isLoading || !privateKeyInput.trim()}
            aria-busy={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
          
          <div className="auth-divider" aria-hidden="true">or</div>
          
          <button 
            type="button"
            onClick={handleCreateAccount} 
            className="auth-button secondary"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create New Account'}
          </button>
        </form>
        
        <aside 
          id="security-note"
          style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: 'rgba(234, 179, 8, 0.1)',
            borderRadius: '8px',
            borderLeft: '4px solid rgb(234, 179, 8)'
          }}
          aria-label="Security information"
        >
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>
            <span aria-hidden="true">🔒</span>
            <strong> Security Note:</strong> Your private key is stored locally on your device 
            and is never sent to any server. Keep your private key safe and never share it with anyone.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default Login;
